import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, fmtDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Package, Factory, Truck, FileText, Wallet, Upload, X } from "lucide-react";
import { usePager, Pager } from "@/components/Pager";

const kpis = [
  { key: "active_orders", label: "Active Orders", icon: Package, bg: "#3B82F6", hint: "in progress", to: "/portal/orders" },
  { key: "in_production", label: "In Production", icon: Factory, bg: "#8B5CF6", hint: "on the floor", to: "/portal/orders" },
  { key: "ready_dispatched", label: "Ready / Dispatched", icon: Truck, bg: "#10B981", hint: "moving to you", to: "/portal/dispatch" },
  { key: "pending_quotations", label: "Pending Quotations", icon: FileText, bg: "#F97316", hint: "awaiting your review", to: "/portal/quotations" },
  { key: "outstanding", label: "Outstanding", icon: Wallet, bg: "#F43F5E", hint: "payable balance", currency: true, to: "/portal/invoices" },
];

const MiniBars = ({ color }) => (
  <span className="inline-flex items-end gap-[2px] ml-auto">
    {[5, 9, 7, 12].map((h, i) => (
      <span key={i} className="w-[3px] rounded-sm" style={{ height: h, background: color, opacity: 0.35 + i * 0.18 }} />
    ))}
  </span>
);

export default function PortalDashboard() {
  const [d, setD] = useState(null);
  const [poOpen, setPoOpen] = useState(false);
  const [po, setPo] = useState({ po_number: "", po_date: "", remarks: "", file: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const load = () => api.get("/portal/dashboard").then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const uploadPo = async () => {
    if (!po.file) { toast.error("Attach your PO file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", po.file);
      fd.append("po_number", po.po_number);
      fd.append("po_date", po.po_date);
      fd.append("remarks", po.remarks);
      const r = await api.post("/portal/upload-po", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.message);
      setPoOpen(false); setPo({ po_number: "", po_date: "", remarks: "", file: null });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const value = (key, currency) => {
    if (d == null) return <Skeleton className="h-7 w-16" />;
    if (key === "ready_dispatched") return `${d.ready_for_dispatch} / ${d.dispatched}`;
    return currency ? inr(d[key]) : (d[key] ?? 0);
  };

  const dismissDispatch = async (oid) => {
    setD((prev) => ({ ...prev, dispatched_orders: (prev.dispatched_orders || []).filter((o) => o.id !== oid) }));
    try { await api.post(`/portal/orders/${oid}/ack-dispatch`); } catch { /* silent */ }
  };

  const notifPager = usePager(d?.notifications || [], 8);

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#7C3AED] via-[#8B5CF6] to-[#6366F1] p-6 sm:p-8 text-white shadow-[0_16px_40px_rgba(124,58,237,0.25)] fade-up" data-testid="portal-welcome-banner">
        <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/[0.07] pointer-events-none" />
        <div className="absolute -bottom-24 right-40 w-56 h-56 rounded-full bg-white/[0.05] pointer-events-none" />
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/70">{today}</div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight mt-1.5">
          Welcome back{d?.company_name ? `, ${d.company_name}` : ""}!
        </h1>
        <p className="text-sm sm:text-[15px] text-white/85 mt-1.5">Here's a live view of your orders with Hi-Tech Silico.</p>
        <button onClick={() => setPoOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-white text-[#6D28D9] hover:bg-white/90 rounded-full px-4 py-2.5 transition-colors duration-200 shadow-lg"
                data-testid="portal-upload-po-btn">
          <Upload size={13} /> Upload Purchase Order
        </button>
        <div className="hidden sm:flex absolute right-8 top-1/2 -translate-y-1/2 w-24 h-24 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 items-center justify-center">
          <Package size={40} strokeWidth={1.3} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 fade-up" style={{ animationDelay: "80ms" }}>
        {kpis.map(({ key, label, icon: Icon, bg, hint, currency, to }) => (
          <Link key={key} to={to} data-testid={`pkpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Card className="p-5 border-slate-100 rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 transition-all duration-300 h-full bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12px] text-slate-500 font-medium truncate">{label}</div>
                  <div className="font-display text-2xl font-bold text-slate-900 tabular-nums leading-none mt-2.5">
                    {value(key, currency)}
                  </div>
                </div>
                <div className="w-10 h-10 flex-none rounded-xl flex items-center justify-center text-white transition-transform duration-300 hover:scale-110"
                     style={{ background: bg, boxShadow: `0 8px 20px ${bg}45` }}>
                  <Icon size={17} strokeWidth={1.8} />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400">
                <span className="truncate">{hint}</span>
                <MiniBars color={bg} />
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {(d?.dispatched_orders || []).map((o) => (
        <div key={o.id} className="flex items-center gap-3 border border-sky-200 bg-sky-50 rounded-xl px-4 py-3 fade-up"
             data-testid={`order-dispatched-banner-${o.id}`}>
          <Truck size={18} className="text-sky-600 shrink-0" />
          <div className="text-sm text-sky-900 flex-1">
            <span className="font-semibold">Order {o.number} dispatched</span>
            {o.transporter ? ` via ${o.transporter}` : ""}{o.lr_number ? ` (LR ${o.lr_number})` : ""} — it's on the way!
            {o.track_url ? (
              <a href={o.track_url} target="_blank" rel="noreferrer"
                 className="ml-2 font-semibold underline underline-offset-2 hover:text-sky-700"
                 data-testid={`order-dispatched-track-${o.id}`}>Track shipment live</a>
            ) : o.tracking_token ? (
              <a href={`/track/${o.tracking_token}`} target="_blank" rel="noreferrer"
                 className="ml-2 font-semibold underline underline-offset-2 hover:text-sky-700"
                 data-testid={`order-dispatched-track-${o.id}`}>Track shipment live</a>
            ) : (
              <Link to="/portal/dispatch" className="ml-2 font-semibold underline underline-offset-2 hover:text-sky-700"
                    data-testid={`order-dispatched-view-${o.id}`}>View order</Link>
            )}
          </div>
          <button onClick={() => dismissDispatch(o.id)} className="p-1 text-sky-600 hover:text-sky-800"
                  title="Dismiss" data-testid={`order-dispatched-dismiss-${o.id}`}>
            <X size={15} />
          </button>
        </div>
      ))}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 fade-up" style={{ animationDelay: "160ms" }}>
        <Card className="lg:col-span-8 p-6 border-slate-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="portal-notifications">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Latest Updates</div>
          <div className="space-y-4">
            {notifPager.pageItems.length ? notifPager.pageItems.map((n, i) => (
              <div key={i} className="flex gap-3 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-2 flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-700">{n.text}</div>
                </div>
                <div className="text-[11px] text-slate-400 whitespace-nowrap">{fmtDate(n.at)}</div>
              </div>
            )) : <p className="text-sm text-slate-400">No updates yet.</p>}
          </div>
          <div className="mt-4 -mx-6 -mb-6"><Pager pager={notifPager} /></div>
        </Card>

        <Card className="lg:col-span-4 p-6 border-slate-200 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="portal-quick-actions">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">Quick Actions</div>
          <div className="space-y-2">
            {[["Browse Catalogue & Request Quote", "/portal/catalogue"],
              ["View & Accept Quotations", "/portal/quotations"],
              ["Track Orders Live", "/portal/orders"],
              ["Download Invoices", "/portal/invoices"],
              ["Test Certificates & Documents", "/portal/documents"],
              ["Raise a Support Request", "/portal/support"]].map(([label, to]) => (
              <Link key={to} to={to}
                    className="block text-sm border border-slate-200 rounded-lg px-3.5 py-2.5 text-slate-700 hover:border-[#3B82F6]/40 hover:bg-slate-50 transition-colors duration-200">
                {label}
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {poOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={() => setPoOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="portal-po-dialog">
            <h2 className="font-display font-bold text-slate-900 mb-4">Upload Purchase Order</h2>
            <div className="space-y-3 text-sm">
              <input className="w-full border border-slate-200 rounded-md px-3 py-2" placeholder="PO Number"
                     value={po.po_number} onChange={(e) => setPo({ ...po, po_number: e.target.value })} data-testid="po-number" />
              <input type="date" className="w-full border border-slate-200 rounded-md px-3 py-2"
                     value={po.po_date} onChange={(e) => setPo({ ...po, po_date: e.target.value })} data-testid="po-date" />
              <textarea className="w-full border border-slate-200 rounded-md px-3 py-2" rows={2} placeholder="Remarks (optional)"
                        value={po.remarks} onChange={(e) => setPo({ ...po, remarks: e.target.value })} data-testid="po-remarks" />
              <input type="file" accept=".pdf,image/*" onChange={(e) => setPo({ ...po, file: e.target.files?.[0] || null })} data-testid="po-file"
                     className="w-full text-xs" />
              <div className="flex gap-2 pt-1">
                <button onClick={uploadPo} disabled={busy} className="flex-1 bg-[#3B82F6] text-white rounded-md py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="po-submit">
                  {busy ? "Uploading…" : "Submit PO"}</button>
                <button onClick={() => setPoOpen(false)} className="px-4 border border-slate-200 rounded-full text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
